import { type QCardActionsProps, type QDialog, useQuasar } from 'quasar';
import { computed, type ComputedRef, getCurrentInstance, type Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { BTN_NEGATIVE_LG, BTN_PRIMARY_LG } from '@/constants/ui/buttons.constants';
import { Position } from '@/constants/ui/types.utils';

interface IDialogProperties {
    dialog: Ref<QDialog | null>;

    getPositionValue: ComputedRef<Position>;
    getConfirmBtnStyle: ComputedRef<typeof BTN_NEGATIVE_LG | typeof BTN_PRIMARY_LG>;
    getConfirmLabelValue: ComputedRef<string>;
    getCancelLabelValue: ComputedRef<string>;
    getActionsAlign: ComputedRef<QCardActionsProps['align']>;
    isDisabledCancelButton: ComputedRef<boolean>;
    isDisabledConfirmButton: ComputedRef<boolean>;

    onCancelClick: (eventValue: unknown) => void;
    onConfirmClick: (eventValue: unknown) => void;
    onSecondaryClick: (eventValue: unknown) => void;
    onQuasarDialogHide: () => void;
    onQuasarDialogShow: () => void;
    onEmitHide: () => void;
}

export interface IDialogProps {
    position?: Position;
    negativeStyle?: boolean;
    confirmLabel?: string;
    cancelLabel?: string;
    loading?: boolean;
    disableCancel?: boolean;
    disableConfirm?: boolean;
    hideCancel?: boolean;
}

export function useDialogProperties(props: IDialogProps): IDialogProperties {
    const q = useQuasar();
    const { t } = useI18n();
    const { emit } = getCurrentInstance();

    const dialog = ref<QDialog | null>(null);

    const getPositionValue = computed<Position>(() => {
        if (props.position) {
            return props.position;
        }
        return q.screen.lt.md ? Position.BOTTOM : Position.STANDARD;
    });
    const getConfirmBtnStyle = computed<typeof BTN_NEGATIVE_LG | typeof BTN_PRIMARY_LG>(() => {
        return props.negativeStyle ? BTN_NEGATIVE_LG : BTN_PRIMARY_LG;
    });
    const getConfirmLabelValue = computed<string>(() => {
        return props.confirmLabel ?? t('ACTION.CONFIRM');
    });
    const getCancelLabelValue = computed<string>(() => {
        return props.cancelLabel ?? t('ACTION.CANCEL');
    });
    const getActionsAlign = computed<QCardActionsProps['align']>(() => {
        return props.hideCancel ? 'right' : 'between';
    });
    const isDisabledCancelButton = computed<boolean>(() => {
        return props.loading || props.disableCancel;
    });
    const isDisabledConfirmButton = computed<boolean>(() => {
        return props.loading || props.disableConfirm;
    });

    const onCancelClick = (eventValue: unknown): void => {
        emit('cancel', eventValue);
    };
    const onConfirmClick = (eventValue: unknown): void => {
        emit('confirm', eventValue);
    };
    const onSecondaryClick = (eventValue: unknown): void => {
        emit('secondary', eventValue);
    };
    const onQuasarDialogHide = (): void => {
        dialog.value?.hide();
    };
    const onQuasarDialogShow = (): void => {
        dialog.value?.show();
    };
    const onEmitHide = (): void => {
        emit('hide');
    };

    return {
        dialog,

        getPositionValue,
        getConfirmBtnStyle,
        getConfirmLabelValue,
        getCancelLabelValue,
        getActionsAlign,
        isDisabledCancelButton,
        isDisabledConfirmButton,

        onCancelClick,
        onConfirmClick,
        onSecondaryClick,
        onQuasarDialogHide,
        onQuasarDialogShow,
        onEmitHide
    };
}
